import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserX, Filter, Calendar, MapPin, Users2, Activity, TrendingDown, Clock, Search, BarChart3, Loader, Crown } from 'lucide-react';

const InactiveUsers = () => {
  const navigate = useNavigate();
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cities, setCities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    days: 7,
    city: '',
    group: '',
    search: ''
  });

  const observer = useRef();

  useEffect(() => {
    fetchCities();
    fetchGroups();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setInactiveUsers([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasMore(true);
    fetchInactiveUsers(true);
  }, [filters.days, filters.city, filters.group]);

  const fetchInactiveUsers = async (reset = false) => {
    try {
      const currentPage = reset ? 1 : pagination.page;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        days: filters.days,
        city: filters.city,
        group: filters.group,
        page: currentPage,
        limit: pagination.limit
      });
      
      const response = await axios.get(`/users/inactive?${params}`);
      
      // Handle the new paginated response structure
      let users = [];
      let paginationData = {};
      
      if (response.data && Array.isArray(response.data.users)) {
        users = response.data.users;
        paginationData = response.data.pagination || {};
      } else if (response.data && Array.isArray(response.data)) {
        // Fallback for old response format
        users = response.data;
        paginationData = {
          page: currentPage,
          totalPages: 1,
          hasMore: false,
          total: response.data.length
        };
      } else {
        console.warn('Unexpected API response structure:', response.data);
        users = [];
        paginationData = {
          page: currentPage,
          totalPages: 0,
          hasMore: false,
          total: 0
        };
      }

      // Apply client-side search filter if search term exists
      if (filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase();
        users = users.filter(user => 
          (user.name && user.name.toLowerCase().includes(searchTerm)) ||
          (user.first_name && user.first_name.toLowerCase().includes(searchTerm)) ||
          (user.last_name && user.last_name.toLowerCase().includes(searchTerm)) ||
          (user.email && user.email.toLowerCase().includes(searchTerm)) ||
          (user.city && user.city.toLowerCase().includes(searchTerm))
        );
      }

      if (reset) {
        setInactiveUsers(users);
      } else {
        setInactiveUsers(prevUsers => [...prevUsers, ...users]);
      }

      setPagination({
        page: paginationData.page || currentPage,
        limit: paginationData.limit || 20,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 1
      });

      setHasMore(paginationData.hasMore || false);

    } catch (error) {
      console.error('Error fetching inactive users:', error);
      if (reset) {
        setInactiveUsers([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get('/cities');
      if (response.data && Array.isArray(response.data)) {
        setCities(response.data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/groups');
      if (response.data && Array.isArray(response.data)) {
        setGroups(response.data);
      } else if (response.data && Array.isArray(response.data.groups)) {
        setGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const clearFilters = () => {
    setFilters({
      days: 7,
      city: '',
      group: '',
      search: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const viewUserStats = (userId) => {
    navigate(`/users/${userId}/stats`);
  };

  const loadMoreUsers = useCallback(() => {
    if (hasMore && !loadingMore) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
      fetchInactiveUsers(false);
    }
  }, [hasMore, loadingMore, pagination.page]);

  // Infinite scroll observer
  const lastUserElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMoreUsers();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadingMore, loadMoreUsers]);

  // Handle search filter changes with debouncing effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Client-side search doesn't require API call, just re-filter existing data
      fetchInactiveUsers(true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Inactive User Re-engagement</h2>
        <p className="text-gray-600 mt-2">Identify and re-engage users who haven't been active recently</p>
      </div>

      {/* Enhanced Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, city..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Inactive Period
            </label>
            <select
              value={filters.days}
              onChange={(e) => setFilters(prev => ({ ...prev, days: e.target.value }))}
              className="input-field"
            >
              <option value="7">7+ days inactive</option>
              <option value="14">14+ days inactive</option>
              <option value="30">30+ days inactive</option>
              <option value="60">60+ days inactive</option>
              <option value="90">90+ days inactive</option>
              <option value="180">6+ months inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <select
              value={filters.city}
              onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
              className="input-field"
            >
              <option value="">All Cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group
            </label>
            <select
              value={filters.group}
              onChange={(e) => setFilters(prev => ({ ...prev, group: e.target.value }))}
              className="input-field"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {(filters.search || filters.city || filters.group || filters.days !== '7') && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Filter className="h-4 w-4" />
              <span>Filters applied</span>
              <span>• {inactiveUsers.length} inactive users found</span>
            </div>
            <button
              onClick={clearFilters}
              className="btn-secondary text-sm"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg p-6 shadow-lg">
                      <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Inactive</p>
                <p className="text-3xl font-bold text-white">{pagination.total || (inactiveUsers || []).length}</p>
              </div>
              <UserX className="h-12 w-12 text-red-200" />
            </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="h-4 w-4 text-red-200 mr-1" />
            <span className="text-red-100 text-sm">Users needing attention</span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Never Active</p>
              <p className="text-3xl font-bold text-white">
                {(inactiveUsers || []).filter(user => !user.last_activity_date).length}
              </p>
            </div>
            <Calendar className="h-12 w-12 text-orange-200" />
          </div>
          <div className="mt-4 flex items-center">
            <Clock className="h-4 w-4 text-orange-200 mr-1" />
            <span className="text-orange-100 text-sm">No activity recorded</span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Avg. Days Inactive</p>
              <p className="text-3xl font-bold text-white">
                {(inactiveUsers || []).length > 0 
                  ? Math.round((inactiveUsers || []).reduce((sum, user) => sum + (user.days_inactive || 0), 0) / (inactiveUsers || []).length)
                  : 0
                }
              </p>
            </div>
            <Activity className="h-12 w-12 text-yellow-200" />
          </div>
          <div className="mt-4 flex items-center">
            <Filter className="h-4 w-4 text-yellow-200 mr-1" />
            <span className="text-yellow-100 text-sm">Average inactivity</span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Critically Inactive</p>
              <p className="text-3xl font-bold text-white">
                {(inactiveUsers || []).filter(user => user.days_inactive >= 60).length}
              </p>
            </div>
            <UserX className="h-12 w-12 text-purple-200" />
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="h-4 w-4 text-purple-200 mr-1" />
            <span className="text-purple-100 text-sm">60+ days inactive</span>
          </div>
        </div>
      </div>

      {/* Inactive Users Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Inactive Users ({(inactiveUsers || []).length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {filters.days}+ days without activity
              {filters.city && ` in ${filters.city}`}
              {filters.group && ` from selected group`}
              {pagination.total > 0 && ` • ${pagination.total} total found`}
            </p>
          </div>
          {/* Future: Re-engagement tools will be added here */}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
                ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location & Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activity Stats
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inactivity Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(inactiveUsers || []).map((user, index) => {
                  const isLast = inactiveUsers.length === index + 1;
                  return (
                    <tr 
                      key={user.id} 
                      ref={isLast ? lastUserElementRef : null}
                      className="table-row cursor-pointer hover:bg-gray-50" 
                      onClick={() => viewUserStats(user.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium text-lg">
                              {(user.name || user.first_name || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || `${user.first_name} ${user.last_name}`}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            {user.phone && (
                              <div className="text-xs text-gray-400">{user.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          {user.city}
                        </div>
                        {user.group_name && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Users2 className="h-4 w-4 mr-1 text-gray-400" />
                            {user.group_name}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 capitalize mt-1">{user.gender}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.total_steps?.toLocaleString() || 0} steps
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.total_distance?.toFixed(1) || 0} km • {user.activity_count || 0} activities
                        </div>
                        <div className="text-xs text-gray-400">
                          {user.total_calories?.toLocaleString() || 0} calories
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(user.last_activity_date)}
                        </div>
                        {user.last_activity_date && (
                          <div className="text-xs text-gray-500">
                            {Math.floor((new Date() - new Date(user.last_activity_date)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                          user.days_inactive >= 90 
                            ? 'bg-red-100 text-red-800'
                            : user.days_inactive >= 60
                            ? 'bg-orange-100 text-orange-800'
                            : user.days_inactive >= 30
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.membership_type === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                          {user.days_inactive ? `${user.days_inactive} days` : 'Never active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            viewUserStats(user.id);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Detailed Stats"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading more inactive users...</span>
            </div>
          )}

          {/* End of Results Indicator */}
          {!hasMore && inactiveUsers.length > 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">You've reached the end of the inactive users list</p>
              <p className="text-sm text-gray-400 mt-1">
                Showing all {pagination.total} inactive users
                             </p>
             </div>
           )}
          </>
        )}

        {!loading && (!inactiveUsers || inactiveUsers.length === 0) && (
          <div className="text-center py-12">
            <UserX className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No inactive users</h3>
            <p className="mt-1 text-sm text-gray-500">
              All users have been active within the selected timeframe.
            </p>
          </div>
        )}
      </div>

      {/* Future: Re-engagement features will be added here */}
    </div>
  );
};

export default InactiveUsers; 